import {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation} from "react-i18next";
import {Icon, Spinner} from "@blueprintjs/core";
import moment from "moment";

import Button from "../common/components/button";
import {blueColor, greenColor, orangeColor, primaryColor, textColor} from "../common/libs/theme-lib";
import TextInput from "../common/components/text-input";
import SelectInput from "../common/components/select-input";
import RenderContent from "../common/components/render-content";
import ClientSelector from "../common/components/client-selector";
import SwitchDateFilters from "../common/components/switch-date-filters";
import Tooltip from "../common/components/tooltip";
import TransportMap from "./components/transport-map";
import {useAuth} from "../auth/auth";
import useLocalStorage from "../common/hooks/use-local-storage";
import {useFilters} from "../filters/filters";
import {useUserClientsData} from "../user-clients-data/user-clients-data";
import {readTransportation, refreshTransportation} from "./transportation-actions";
import {cleanDeliveryNotesState} from "../delivery-notes/delivery-notes-actions";
import {cleanTechnicalsSheetsState} from "../technical-sheets/technicals-sheets-actions";
import {transportationSelector} from "./transportation-selectors";
import transportStatuses from "./transport-statuses.json";

const selector = createSelector(transportationSelector, data => data?.toJS() || []);
const statusColors = {"0": textColor, "1": greenColor, "2": orangeColor, "3": primaryColor, "4": blueColor};
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;
const disabledIconColor = "#94a3b8";

const TransportationPage = () => {
    const auth = useAuth();
    const dispatch = useDispatch();
    const {t} = useTranslation("transportation");
    const {selectedClient} = useAuth();
    const {userClientsData} = useUserClientsData();
    const transportation = useSelector(selector);
    const filtersApi = useFilters();

    const [documentId, setDocumentId] = useState(filtersApi.transportationFilters?.documentId || "");
    const [material, setMaterial] = useState(filtersApi.transportationFilters?.material || "");
    const [transportStatus, setTransportStatus] = useState(filtersApi.transportationFilters?.transportStatus || "");
    const [fromDate, setFromDate] = useState(moment(filtersApi.dateFilters?.fromDate || null));
    const [toDate, setToDate] = useState(moment(filtersApi.dateFilters?.toDate || null));
    const [isManualRange, setIsManualRange] = useState(filtersApi.dateFilters?.isManualRange === true);
    const [predefinedRangeValue, setPredefinedRangeValue] = useState(filtersApi.dateFilters?.predefinedValue || "");
    const [isDateChanged, setIsDateChanged] = useState(false);
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(!filtersApi.transportationFilters);
    const [selectedTransport, setSelectedTransport] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshByClient, setLastRefreshByClient] = useLocalStorage("ac-extranet.transportationLastRefresh");
    const [now, setNow] = useState(() => Date.now());

    const client = userClientsData.find(item => item._id === selectedClient);
    const filters = {
        documentId, material, fromDate, toDate,
        ...(transportStatus && {transport_status: transportStatus})
    };
    const load = () => dispatch(readTransportation({client: selectedClient, ...filters}));
    const groupedData = useMemo(() => groupTransports(transportation), [transportation]);
    const lastUpdate = useMemo(() => latestUpdate(transportation), [transportation]);
    const statusOptions = transportStatuses.map(status => ({
        value: status.value,
        label: status.value === "" ? t("filters.statusAll") : t(`statuses.${status.value}`)
    }));

    // La actualización manual se bloquea 5 minutos desde la última vez que se hizo
    // (se toma como referencia la más reciente entre los datos y el último refresco manual guardado)
    const refreshLockUntil = useMemo(() => {
        const dataLastUpdate = lastUpdate ? moment(lastUpdate).valueOf() : 0;
        const manualLastRefresh = (lastRefreshByClient && lastRefreshByClient[selectedClient]) || 0;
        return Math.max(dataLastUpdate, manualLastRefresh) + REFRESH_COOLDOWN_MS;
    }, [lastUpdate, lastRefreshByClient, selectedClient]);
    const isRefreshLocked = refreshLockUntil > now;

    // Comprueba cada 30 segundos si ha pasado el bloqueo de 5 minutos
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 30 * 1000);
        return () => clearInterval(interval);
    }, []);

    const refresh = async () => {
        if (isRefreshLocked) return;
        setIsRefreshing(true);
        try {
            await dispatch(refreshTransportation(selectedClient, filters));
            setLastRefreshByClient({...lastRefreshByClient, [selectedClient]: Date.now()});
            setShowWelcomeMessage(false);
            setSelectedTransport(null);
        } finally {
            setNow(Date.now());
            setIsRefreshing(false);
        }
    };

    const search = () => {
        const filters = {documentId, material, transportStatus};
        if (isDateChanged) {
            dispatch(cleanDeliveryNotesState());
            dispatch(cleanTechnicalsSheetsState());
            filtersApi.removeDeliveryNotesFilters();
            filtersApi.removeTechnicalSheetsFilters();
        }
        load();
        filtersApi.setTransportationFilters(filters);
        filtersApi.setDateFilters({fromDate, toDate, isManualRange, predefinedValue: predefinedRangeValue});
        setShowWelcomeMessage(false);
    };

    return (
        <>
            <header style={{display: "flex", alignItems: "flex-end", paddingBottom: 15}}>
                <div style={{flex: 1}}>
                    <div style={{color: primaryColor, fontSize: 28, fontWeight: "bold", marginBottom: 10}}>{t("title", {client: client?.name})}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                {auth.userType === "admin" && <ClientSelector />}
                <TextInput label={t("filters.transportCode")} style={{marginRight: 15, width: 130}} value={documentId} setValue={setDocumentId} />
                <TextInput label={t("filters.material")} style={{marginRight: 15, width: 130}} value={material} setValue={setMaterial} />
                <div style={{marginRight: 15}}>
                    <SelectInput 
                        options={statusOptions} 
                        label={t("filters.status")} 
                        onChange={option => setTransportStatus(option.value)} 
                        value={statusOptions.find(option => option.value === transportStatus)} 
                    />
                </div>
                <SwitchDateFilters 
                    fromValue={fromDate} 
                    setFromValue={setFromDate} 
                    toValue={toDate} 
                    setToValue={setToDate} 
                    isManualDateRange={isManualRange} 
                    setIsManualDateRange={setIsManualRange} 
                    predefinedValue={predefinedRangeValue} 
                    setPredefinedValue={setPredefinedRangeValue} 
                    setDateChanged={setIsDateChanged} 
                />
                <Button 
                    text={t("common:actions.search")} 
                    action={search} 
                    intent="primary" 
                />
            </header>
            <RenderContent 
                nameCollection={t("nameCollection")} 
                data={groupedData} 
                showWelcomeMessage={showWelcomeMessage} 
                renderHeader={true}
                renderHtml={lastUpdate && 
                    (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div style={{fontSize: 14}}>
                                <strong>{groupedData.length}</strong> {t("results")}
                            </div>
                            <LastUpdate 
                                value={lastUpdate} 
                                refreshing={isRefreshing} 
                                locked={isRefreshLocked} 
                                onRefresh={refresh} 
                                t={t} 
                            />
                        </div>
                    )
                } 
                config={{
                    sortable: true, 
                    showFooter: true, 
                    initialState: {sortBy: [{id: "departure_date", desc: true}]}}} 
                columns={columns(t, setSelectedTransport)} 
            />
            {selectedTransport && 
                <TransportMap 
                    transport={selectedTransport} 
                    t={t} 
                    onClose={() => setSelectedTransport(null)}
                    client={client} 
                />
            }
        </>
    );
};

const LastUpdate = ({value, refreshing, locked, onRefresh, t}) => {
    const isDisabled = refreshing || locked;

    const refreshButton = (
        <button 
            type="button" 
            onClick={onRefresh} 
            disabled={isDisabled} 
            title={t("refresh")} 
            aria-disabled={isDisabled}
            style={{
                border: 0, 
                background: "transparent", 
                color: isDisabled ? disabledIconColor : primaryColor, 
                cursor: isDisabled ? "default" : "pointer", 
                display: "flex", 
                alignItems: "center"
            }}
        >
            {refreshing ? <Spinner size={18}/> : <Icon icon="refresh" />}
        </button>
    );

    return (
        <div style=
            {{
                display: "flex", 
                justifyContent: "flex-end", 
                alignItems: "center", 
                gap: 8, 
                marginBottom: 10, 
                fontSize: 14
            }}
        >
            {t("lastUpdate")}: <strong>{moment(value).format("DD/MM/YYYY HH:mm:ss")}</strong>
            {locked ? (
                <Tooltip position="bottom" content={t("refreshCooldown")}>
                    <span style={{display: "flex", alignItems: "center"}}>
                        {refreshButton}
                    </span>
                </Tooltip>
            ) : refreshButton}
        </div>
    );
};
const columns = (t, setSelectedTransport) => [
    {
        id: "departure_date", 
        Header: t("transportationTableColumns.departure_date"), 
        accessor: row => row.actual_start_date || row.planned_start_date, 
        Cell: ({row, value}) => {
            const dateLabel = formatDateTime(value);
            return (
                <div style={{display: "flex", alignItems: "center", gap: 6}}>
                    <span>{dateLabel}</span>
                    {row.canExpand && (
                        <span {...row.getToggleRowExpandedProps()} style={{cursor: "pointer", display: "flex"}}>
                            <Icon icon={row.isExpanded ? "chevron-down" : "chevron-right"} />
                        </span>
                    )}
                </div>
            );
        },
        maxWidth: 100
    },
    {
        id: "transport_number", 
        Header: t("transportationTableColumns.transport_code"), 
        accessor: "transport_number", 
        maxWidth: 100
    },
    {
        id: "client_material", 
        Header: t("transportationTableColumns.material"), 
        accessor: row => row.subRows ? "-" : row.client_material,
        maxWidth: 100
    },
    {
        id: "client_material_description", 
        Header: t("transportationTableColumns.description"), 
        accessor: row => row.subRows ? "-" : row.client_material_description,
    },
    {
        id: "planned_arrival", 
        Header: t("transportationTableColumns.planned_arrival"), 
        accessor: row => row.subRows ? (formatDateTime(row.planned_end_date, row.planned_end_time) || "-") : "",
        maxWidth: 100
    },
    {
        id: "actual_arrival", 
        Header: t("transportationTableColumns.actual_arrival"), 
        accessor: row => row.subRows ? (formatDateTime(row.actual_end_date, row.actual_end_time) || "-") : "", 
        maxWidth: 100
    },
    {
        id: "status", 
        Header: t("transportationTableColumns.status"), 
        accessor: row => row.transport_status != null ? 
            {
                label: t(`statuses.${row.transport_status}`), 
                status: row.transport_status
            } : 
            null
        , 
        type: "function", 
        nodeContent: value => value && 
            <span style={{
                padding: "5px 9px", 
                borderRadius: 5, 
                color: "white", 
                backgroundColor: statusColors[value.status] || statusColors["0"], 
                fontSize: 12
            }}>
                {value.status in statusColors ? value.label : t("statuses.other")}
            </span>,
        maxWidth: 100
    },
    {
        id: "location", 
        Header: t("transportationTableColumns.location"), 
        accessor: row => row, 
        type: "function", 
        disableSortBy: true, 
        nodeContent: row => row.subRows ?
            <button 
                type="button" 
                onClick={() => setSelectedTransport(toMapTransport(row))} 
                style={{
                    border: 0, 
                    background: "transparent", 
                    color: blueColor, 
                    textDecoration: "underline", 
                    cursor: "pointer"
                }}
            >
                {t("viewLocation")}
            </button> :
            null,
        maxWidth: 100
    }
];
const groupTransports = transportation => Object.values(transportation.reduce((groups, item) => {
    if (!groups[item.transport_number]) groups[item.transport_number] = {...item, subRows: []};
    groups[item.transport_number].subRows.push({
        client_material: item.client_material,
        client_material_description: item.client_material_description
    });
    return groups;
}, {}));
const latestUpdate = transportation => 
    transportation.reduce((latest, item) => 
        !latest || moment(item.updatedAt).isAfter(latest) ? item.updatedAt : latest, null);
const toMapTransport = row => 
    ({
        ...row, 
        departure_date: formatDateTime(row.actual_start_date || row.planned_start_date), 
        planned_arrival: formatDateTime(row.planned_end_date, row.planned_end_time), 
        actual_arrival: formatDateTime(row.actual_end_date, row.actual_end_time), 
        last_update: row.updatedAt ? moment(row.updatedAt).format("DD/MM/YYYY HH:mm:ss") : ""
    });
const formatDateTime = (date, time) => {
    if (!date || date === "0000-00-00") return ""; 
    const parsed = moment(
        time ? `${date} ${time}` : date, 
        ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYYMMDD HHmmss", "YYYYMMDD HH:mm:ss", "YYYY-MM-DD"], 
        true
    ); 
    return parsed.isValid() ? 
        parsed.format(time ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY") : 
        `${date}${time ? ` ${time}` : ""}`; 
    };

export default TransportationPage;
